import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, PlayCircle, FileText, CheckCircle2, RotateCcw, GraduationCap, ExternalLink, Pencil, Book, ChevronRight, Clock, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { celebrate } from "@/lib/celebrate";

function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // Vimeo
  m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
}

function VideoView({ lesson }) {
  if (!lesson) return null;
  if (lesson.video_type === "url") {
    const embed = getEmbedUrl(lesson.video_url);
    if (embed) {
      return (
        <div className="video-shell">
          <iframe
            src={embed}
            title={lesson.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="lesson-iframe"
          />
        </div>
      );
    }
    return (
      <div className="video-shell flex items-center justify-center text-white p-12">
        <a
          href={lesson.video_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 underline"
        >
          <ExternalLink className="h-4 w-4" /> Abrir video en nueva pestaña
        </a>
      </div>
    );
  }
  // uploaded file
  const src = `${API}/files/${lesson.video_url}`;
  return (
    <div className="video-shell">
      <video controls src={src} data-testid="lesson-video" />
    </div>
  );
}

function PdfView({ pdf }) {
  if (!pdf) return null;
  const src = pdf.pdf_type === "url" ? pdf.pdf_url : `${API}/files/${pdf.pdf_url}`;
  return (
    <div className="bg-[#F3F4F6] border border-[#E5E7EB] rounded-md overflow-hidden">
      <iframe
        src={src}
        title={pdf.title}
        className="w-full"
        style={{ height: "70vh" }}
        data-testid="pdf-iframe"
      />
    </div>
  );
}

function QuizView({ quiz, onSubmitted }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { id: courseId } = useParams();

  if (!quiz) return null;

  const handleSubmit = async () => {
    if (Object.keys(answers).length < quiz.questions.length) {
      toast.error("Responde todas las preguntas antes de enviar.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/quiz/submit`, { answers });
      setResult(data);
      onSubmitted?.(data);
      if (data.passed) {
        toast.success(`¡Aprobado con ${data.score}%!`);
        celebrate("course_passed");
      } else {
        toast.error(`Obtuviste ${data.score}%. Revisa el material e intenta nuevamente.`);
      }
      // points awarded toasts
      (data.points_awarded || []).forEach((p) => {
        toast.success(`+${p.points} pts · ${p.reason}`, { duration: 4500 });
        celebrate("points_earned");
      });
    } catch (e) {
      toast.error("Error al enviar el examen.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#E5E7EB] rounded-md p-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Examen</p>
          <p className="text-xs text-[#737373]">Aprobación: {quiz.pass_score}%</p>
        </div>
        <h3 className="font-heading text-xl text-[#1A1A1A]">{quiz.title}</h3>
        <p className="text-sm text-[#374151] mt-1">
          {quiz.questions.length} preguntas · selecciona la mejor respuesta.
        </p>
      </div>

      {quiz.questions.map((q, qi) => (
        <div key={q.id} className="bg-white border border-[#E5E7EB] rounded-md p-6" data-testid={`question-${q.id}`}>
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">Pregunta {qi + 1}</p>
          <p className="font-heading text-base text-[#1A1A1A] mb-4">{q.text}</p>
          <div className="space-y-2">
            {q.options.map((opt) => {
              const selected = answers[q.id] === opt.id;
              const detail = result?.detail?.find((d) => d.question_id === q.id);
              const isCorrect = detail?.correct === opt.id;
              const isSelectedAndWrong = detail && detail.selected === opt.id && !detail.is_correct;
              return (
                <label
                  key={opt.id}
                  className={`flex gap-3 p-3 border rounded-md cursor-pointer transition-colors duration-150
                    ${selected && !result ? "border-[#1B2E7A] bg-[#F3F4F6]" : "border-[#E5E7EB] hover:bg-[#FFFFFF]"}
                    ${result && isCorrect ? "border-[#3A5A40] bg-[#3A5A40]/10" : ""}
                    ${result && isSelectedAndWrong ? "border-[#DC2626] bg-[#DC2626]/10" : ""}`}
                  data-testid={`option-${opt.id}`}
                >
                  <input
                    type="radio"
                    name={q.id}
                    checked={selected}
                    onChange={() => !result && setAnswers((p) => ({ ...p, [q.id]: opt.id }))}
                    disabled={!!result}
                    className="mt-1 accent-[#1B2E7A]"
                  />
                  <span className="text-sm text-[#1A1A1A]">{opt.text}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}

      {!result ? (
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
          data-testid="submit-quiz-button"
        >
          {submitting ? "Enviando..." : "Enviar examen"}
        </Button>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Resultado</p>
            <p className="font-heading text-3xl text-[#1A1A1A]">{result.score}%</p>
            <p className={`text-sm mt-1 ${result.passed ? "text-[#3A5A40]" : "text-[#DC2626]"}`}>
              {result.passed ? "¡Aprobado!" : "No aprobado"} · {result.correct} de {result.total} correctas
            </p>
          </div>
          <Button onClick={reset} variant="outline" className="border-[#E5E7EB]" data-testid="retake-quiz-button">
            <RotateCcw className="h-4 w-4 mr-2" /> Volver a intentar
          </Button>
        </div>
      )}
    </div>
  );
}

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [activePdfId, setActivePdfId] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [scenarios, setScenarios] = useState([]);

  const fetchCourse = async () => {
    try {
      const r = await api.get(`/courses/${id}`);
      setData(r.data);
      if (r.data.lessons[0]) setActiveLessonId((cur) => cur || r.data.lessons[0].id);
      if (r.data.pdfs[0]) setActivePdfId((cur) => cur || r.data.pdfs[0].id);
      // Pre/post improvement (only renders when there are ≥2 attempts)
      try {
        const cmp = await api.get(`/courses/${id}/progress-comparison`);
        setComparison(cmp.data);
      } catch (_) {
        setComparison(null);
      }
      // Practice scenarios linked to this course
      try {
        const scn = await api.get(`/scenarios?course_id=${id}`);
        setScenarios(Array.isArray(scn.data) ? scn.data : []);
      } catch (_) {
        setScenarios([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourse();
    // eslint-disable-next-line
  }, [id]);

  if (loading) return <div className="text-[#374151]">Cargando curso...</div>;
  if (!data) return <div className="text-[#374151]">Curso no encontrado.</div>;

  const { course, lessons, pdfs, quiz, progress, books = [] } = data;
  const completedSet = new Set(progress?.completed_lesson_ids || []);
  const activeLesson = lessons.find((l) => l.id === activeLessonId) || lessons[0];
  const activePdf = pdfs.find((p) => p.id === activePdfId) || pdfs[0];
  const bestAttempt = progress?.attempts?.reduce(
    (b, a) => (b == null || a.score > b.score ? a : b),
    null
  );

  const markComplete = async () => {
    if (!activeLesson) return;
    try {
      const { data } = await api.post(`/lessons/${activeLesson.id}/complete`);
      toast.success("Lección marcada como completada");
      if (data?.points_awarded) {
        toast.success(`+${data.points_awarded.points} pts · ${data.points_awarded.reason}`, { duration: 4500 });
      }
      fetchCourse();
    } catch {
      toast.error("Error al marcar la lección");
    }
  };

  return (
    <div data-testid="course-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-[#374151] hover:text-[#1A1A1A] mb-6"
        data-testid="back-button"
      >
        <ChevronLeft className="h-4 w-4" /> Volver
      </button>

      <div className="mb-8 flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">{course.topic}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
            {course.title}
          </h1>
          <p className="text-[#374151] leading-relaxed">{course.description}</p>
          {/* Course meta strip: total duration + capsule count */}
          {lessons.length > 0 && (
            <div className="flex items-center gap-4 mt-4 text-xs text-[#6B7280]" data-testid="course-meta-strip">
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" strokeWidth={1.8} />
                <span>
                  <strong className="text-[#1A1A1A]">
                    {lessons.reduce((sum, l) => sum + (l.duration_min || 0), 0) || lessons.length * 5}
                  </strong>{" "}
                  min total
                </span>
              </span>
              <span className="text-[#D1D5DB]">·</span>
              <span className="inline-flex items-center gap-1.5">
                <PlayCircle className="h-3.5 w-3.5" strokeWidth={1.8} />
                <strong className="text-[#1A1A1A]">{lessons.length}</strong>{" "}
                {lessons.length === 1 ? "capsule" : "capsules"}
              </span>
              {lessons.filter((l) => (l.duration_min || 0) > 0 && (l.duration_min || 0) <= 10).length > 0 && (
                <>
                  <span className="text-[#D1D5DB]">·</span>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#854D0E] text-[10px] uppercase tracking-[0.16em] font-semibold">
                    <Zap className="h-3 w-3" strokeWidth={2.4} />
                    {lessons.filter((l) => (l.duration_min || 0) > 0 && (l.duration_min || 0) <= 10).length} quick
                  </span>
                </>
              )}
            </div>
          )}

          {/* Pre/Post Assessment comparison (EVERFI-style) */}
          {comparison?.has_data && comparison.attempts_count >= 2 && (
            <div
              className="mt-5 inline-flex items-stretch rounded-lg overflow-hidden border border-[#D1D5DB] bg-white"
              data-testid="progress-comparison-widget"
            >
              <div className="px-4 py-3 border-r border-[#D1D5DB]">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-medium mb-0.5">First try</p>
                <p className="text-2xl font-heading text-[#6B7280]" data-testid="comparison-first">{comparison.first.score}%</p>
              </div>
              <div className="px-4 py-3 flex items-center justify-center bg-[#F3F4F6] border-r border-[#D1D5DB]">
                <ChevronRight className="h-5 w-5 text-[#1B2E7A]" strokeWidth={2.2} />
              </div>
              <div className="px-4 py-3 border-r border-[#D1D5DB]">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-medium mb-0.5">Best score</p>
                <p className="text-2xl font-heading text-[#1B2E7A]" data-testid="comparison-best">{comparison.best.score}%</p>
              </div>
              <div
                className="px-4 py-3 flex flex-col justify-center"
                style={{
                  backgroundColor: comparison.delta >= 20 ? "#FEF3C7" : comparison.delta > 0 ? "#F3F4F6" : "#FEE2E2",
                }}
              >
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: comparison.delta >= 20 ? "#854D0E" : comparison.delta > 0 ? "#1B2E7A" : "#991B1B" }}>
                  {comparison.delta >= 0 ? "Improved" : "Dropped"}
                </p>
                <p className="text-xl font-heading" data-testid="comparison-delta" style={{ color: comparison.delta >= 20 ? "#854D0E" : comparison.delta > 0 ? "#1B2E7A" : "#991B1B" }}>
                  {comparison.delta > 0 ? `+${comparison.delta}` : comparison.delta}%
                </p>
              </div>
            </div>
          )}
        </div>

        {bestAttempt && (
          <div className="bg-white border border-[#E5E7EB] rounded-md p-4 min-w-[180px]">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Mejor puntaje</p>
            <p className={`font-heading text-3xl ${bestAttempt.passed ? "text-[#1B2E7A]" : "text-[#DC2626]"}`}>
              {bestAttempt.score}%
            </p>
            <p className="text-xs text-[#737373] mt-0.5">
              {bestAttempt.passed ? "Aprobado" : "No aprobado"}
            </p>
          </div>
        )}

        {user?.role === "admin" && (
          <Link
            to={`/admin/courses/${course.id}`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1B2E7A] text-white text-sm hover:bg-[#13205A] transition-colors"
            data-testid="edit-course-link"
          >
            <Pencil className="h-4 w-4" /> Editar contenido
          </Link>
        )}
      </div>

      {/* Practice scenarios linked to this course */}
      {scenarios.length > 0 && (
        <div className="mb-6 p-5 rounded-xl bg-gradient-to-br from-[#FEF3C7] to-white border border-[#F4B81F]" data-testid="practice-scenarios-strip">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[#854D0E]" strokeWidth={2} />
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#854D0E] font-bold">Practice scenarios</p>
          </div>
          <p className="text-sm text-[#374151] mb-3 max-w-2xl">
            Take what you just learned and practice it in a real sales situation. Each pick gives instant
            feedback and points.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {scenarios.map((s) => (
              <Link
                key={s.id}
                to={`/scenarios/${s.id}`}
                className="block bg-white border border-[#F4B81F] hover:border-[#854D0E] rounded-lg p-4 transition-colors group"
                data-testid={`practice-scenario-${s.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <PlayCircle className="h-4 w-4 text-[#854D0E] group-hover:scale-110 transition-transform" strokeWidth={1.8} />
                  <p className="text-sm font-medium text-[#1A1A1A] truncate flex-1">{s.title}</p>
                  <ChevronRight className="h-4 w-4 text-[#854D0E] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {s.description && (
                  <p className="text-xs text-[#6B7280] line-clamp-2">{s.description}</p>
                )}
                <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-[0.16em] text-[#854D0E] font-bold">
                  <span>{(s.nodes || []).length} steps</span>
                  <span className="text-[#F4B81F]">·</span>
                  <span>{s.pass_points} pts on pass</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="videos" className="w-full">
        <TabsList className="bg-[#F3F4F6] border border-[#E5E7EB]">
          <TabsTrigger value="videos" data-testid="tab-videos" className="data-[state=active]:bg-white">
            <PlayCircle className="h-4 w-4 mr-2" /> Videos ({lessons.length})
          </TabsTrigger>
          <TabsTrigger value="pdfs" data-testid="tab-pdfs" className="data-[state=active]:bg-white">
            <FileText className="h-4 w-4 mr-2" /> Material PDF ({pdfs.length})
          </TabsTrigger>
          <TabsTrigger value="quiz" data-testid="tab-quiz" className="data-[state=active]:bg-white" disabled={!quiz}>
            <GraduationCap className="h-4 w-4 mr-2" /> Examen
          </TabsTrigger>
          {books.length > 0 && (
            <TabsTrigger value="books" data-testid="tab-books" className="data-[state=active]:bg-white">
              <Book className="h-4 w-4 mr-2" /> Libros ({books.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="videos" className="mt-6">
          {lessons.length === 0 ? (
            <div className="text-[#374151] bg-white border border-[#E5E7EB] rounded-md p-8 text-center">
              No hay lecciones disponibles aún.
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <VideoView lesson={activeLesson} />
                <div className="bg-white border border-[#E5E7EB] rounded-md p-5">
                  <h3 className="font-heading text-lg text-[#1A1A1A]">{activeLesson?.title}</h3>
                  {activeLesson?.description && (
                    <p className="text-sm text-[#374151] mt-2 leading-relaxed">{activeLesson.description}</p>
                  )}
                  <div className="mt-4 flex items-center gap-3">
                    <Button
                      onClick={markComplete}
                      disabled={completedSet.has(activeLesson?.id)}
                      className="bg-[#1B2E7A] hover:bg-[#13205A] text-white disabled:opacity-60"
                      data-testid="mark-complete-button"
                    >
                      {completedSet.has(activeLesson?.id) ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-2" /> Completada
                        </>
                      ) : (
                        "Marcar como completada"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <aside className="space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">Lecciones</p>
                {lessons.map((l, idx) => {
                  const isActive = l.id === activeLessonId;
                  const done = completedSet.has(l.id);
                  return (
                    <button
                      key={l.id}
                      onClick={() => setActiveLessonId(l.id)}
                      data-testid={`lesson-item-${l.id}`}
                      className={`w-full text-left px-4 py-3 rounded-md border transition-colors duration-150
                        ${isActive ? "bg-[#F3F4F6] border-[#1B2E7A]" : "bg-white border-[#E5E7EB] hover:bg-[#FFFFFF]"}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-[#F3F4F6] text-[#1B2E7A] text-xs flex items-center justify-center font-medium shrink-0">
                          {idx + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-[#1A1A1A] truncate">{l.title}</p>
                          <p className="text-xs text-[#6B7280] mt-0.5 inline-flex items-center gap-1.5">
                            <Clock className="h-3 w-3" strokeWidth={1.8} />
                            {l.duration_min ? `${l.duration_min} min` : "Video"}
                            {l.duration_min > 0 && l.duration_min <= 10 && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded-full bg-[#FEF3C7] text-[#854D0E] text-[9px] uppercase tracking-[0.14em] font-bold">
                                <Zap className="h-2.5 w-2.5" strokeWidth={2.6} />
                                Quick
                              </span>
                            )}
                          </p>
                        </div>
                        {done && <CheckCircle2 className="h-4 w-4 text-[#1B2E7A]" strokeWidth={2} />}
                      </div>
                    </button>
                  );
                })}
              </aside>
            </div>
          )}
        </TabsContent>

        <TabsContent value="pdfs" className="mt-6">
          {pdfs.length === 0 ? (
            <div className="text-[#374151] bg-white border border-[#E5E7EB] rounded-md p-8 text-center">
              No hay material PDF disponible aún.
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <PdfView pdf={activePdf} />
                <div className="bg-white border border-[#E5E7EB] rounded-md p-5 mt-4">
                  <h3 className="font-heading text-lg text-[#1A1A1A]">{activePdf?.title}</h3>
                  <a
                    className="text-sm text-[#1B2E7A] underline mt-2 inline-flex items-center gap-1"
                    target="_blank"
                    rel="noreferrer"
                    href={activePdf?.pdf_type === "url" ? activePdf?.pdf_url : `${API}/files/${activePdf?.pdf_url}`}
                  >
                    Abrir en nueva pestaña <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
              <aside className="space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">Documentos</p>
                {pdfs.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setActivePdfId(p.id)}
                    data-testid={`pdf-item-${p.id}`}
                    className={`w-full text-left px-4 py-3 rounded-md border transition-colors duration-150 flex items-start gap-3
                      ${p.id === activePdfId ? "bg-[#F3F4F6] border-[#1B2E7A]" : "bg-white border-[#E5E7EB] hover:bg-[#FFFFFF]"}`}
                  >
                    <FileText className="h-4 w-4 text-[#1B2E7A] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span className="text-sm text-[#1A1A1A]">{p.title}</span>
                  </button>
                ))}
              </aside>
            </div>
          )}
        </TabsContent>

        <TabsContent value="quiz" className="mt-6">
          {!quiz ? (
            <div className="text-[#374151] bg-white border border-[#E5E7EB] rounded-md p-8 text-center">
              Aún no hay examen para este curso.
            </div>
          ) : (
            <QuizView quiz={quiz} onSubmitted={fetchCourse} />
          )}
        </TabsContent>

        <TabsContent value="books" className="mt-6">
          {books.length === 0 ? (
            <div className="text-[#374151] bg-white border border-[#E5E7EB] rounded-md p-8 text-center">
              No hay libros relacionados para este curso.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-[#374151] mb-2">
                Estos manuales y playbooks oficiales profundizan los temas de este curso.
                Consúltalos cuando necesites información completa o referencia rápida en el piso de ventas.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {books.map((b) => (
                  <Link
                    key={b.id}
                    to={`/biblioteca/${b.id}`}
                    data-testid={`linked-book-${b.id}`}
                    className="group bg-white border border-[#E5E7EB] hover:border-[#E5E7EB] hover:shadow-md rounded-md p-5 transition-all flex flex-col"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div
                        className="h-12 w-9 rounded-sm flex items-center justify-center shrink-0 relative overflow-hidden"
                        style={{ background: `linear-gradient(155deg, ${b.color_accent || '#1B2E7A'} 0%, ${b.color_accent || '#13205A'}CC 100%)` }}
                      >
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-black/20" />
                        <Book className="h-4 w-4 text-white/80 relative z-10" strokeWidth={1.4} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{b.category}</p>
                        <h4 className="font-heading text-base text-[#1A1A1A] leading-tight mt-0.5">{b.title}</h4>
                      </div>
                    </div>
                    <p className="text-sm text-[#374151] leading-relaxed line-clamp-3 flex-1">{b.description}</p>
                    <div className="mt-3 inline-flex items-center text-sm text-[#1B2E7A] font-medium gap-1 group-hover:gap-2 transition-all">
                      Leer libro
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
